import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import { useState } from 'react';
import React from 'react';
import { StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Props interface for the HorizontalNonLinearStepper component.
 */
interface StepperProps {
  steps: string[];
  submitted: boolean;
  isAllGood: boolean;

  /** Function to render the content of the current step */
  renderStepContent: (step: number) => React.ReactNode;

  /**
   * Optional callback executed when moving to the next step.
   * Should return true to allow progressing to the next step.
   */
  onNext?: (
    step: number,
    e: React.MouseEvent<HTMLButtonElement>
  ) => boolean | undefined;

  /** Callback executed when the user completes all steps */
  onsubmit: () => void;
}

/**
 * A horizontal stepper component with optional control logic per step.
 */
export default function HorizontalNonLinearStepper({
  steps,
  submitted,
  isAllGood,
  renderStepContent,
  onNext,
  onsubmit,
}: StepperProps) {
  // use translation here
  const { t } = useTranslation('components.devices');

  /** Current active step index */
  const [activeStep, setActiveStep] = useState<number>(0);

  /** Returns the total number of steps */
  const totalSteps = (): number => steps.length;

  /** Returns true if the current step is the last one */
  const isLastStep = (): boolean => activeStep === totalSteps() - 1;

  /**
   * Handles progression to the next step.
   * Will only proceed if `onNext` returns true (or is undefined).
   *
   * @param e Mouse click event from the button
   */
  const handleNext = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (onNext) {
      const isGood = !onNext(activeStep, e);

      if (!isLastStep() && isGood) {
        setActiveStep((prev) => prev + 1);
      } else if (isLastStep() && isGood) {
        onsubmit();
      }
    }
  };

  /**
   * Moves to the previous step
   */
  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  /**
   * Sets a specific step as active
   *
   * @param step Index of the step to activate
   */
  const handleStep = (step: number): (() => void) => {
    return () => {
      setActiveStep(step);
    };
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Stepper Header */}
      <Stepper nonLinear activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ mt: 2 }}>{renderStepContent(activeStep)}</Box>

      {/* Step Actions */}
      {
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            {t('Retour')}
          </Button>

          <Box sx={{ flex: '1 1 auto' }} />

          {!isLastStep() ? (
            <Button onClick={handleNext} sx={{ mr: 1 }}>
              {t('Suivant')}
            </Button>
          ) : (
            <Button onClick={handleNext} sx={{ mr: 1 }}>
              {t('Enregistrer')}
            </Button>
          )}
        </Box>
      }
    </Box>
  );
}
